import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Mail, Send, RefreshCw, Search, FileText, Plus, Users, Clock, UserPlus, Check as CheckIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("bc_admin_token") || ""}` });

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function MessagingAdmin() {
  const [view, setView] = useState("inbox"); // inbox | templates | bulk | direct
  return (
    <div data-testid="messaging-tab">
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        {[
          { id: "inbox", label: "Inbox", icon: MessageCircle },
          { id: "templates", label: "Templates", icon: FileText },
          { id: "bulk", label: "Bulk Send", icon: Users },
          { id: "direct", label: "Direct", icon: UserPlus },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setView(t.id)}
            className={`text-xs px-3 py-2 border flex items-center gap-1.5 ${view === t.id ? "border-red-600 bg-red-700/10 text-white" : "border-white/10 text-zinc-400 hover:text-white"}`}
            style={{ borderRadius: 0 }}
            data-testid={`msg-view-${t.id}`}
          >
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>
      {view === "inbox" && <Inbox />}
      {view === "templates" && <Templates />}
      {view === "bulk" && <BulkSender />}
      {view === "direct" && <DirectSender />}
    </div>
  );
}

// ============================================================
// Inbox
// ============================================================
function Inbox() {
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/messaging/threads`, { headers: authHeaders() });
      setThreads(data);
    } catch (e) { toast.error("Could not load threads"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);

  const openThread = async (t) => {
    setSelected(t);
    setThread([]);
    try {
      const { data } = await axios.get(`${API}/admin/messaging/thread/${encodeURIComponent(t.key)}`, { headers: authHeaders() });
      setThread(data);
    } catch (e) { toast.error("Could not load thread"); }
  };

  const filtered = threads.filter((t) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (t.lead_name || "").toLowerCase().includes(q) || (t.lead_email || "").toLowerCase().includes(q) || (t.phone_digits || "").includes(q) || (t.last_message || "").toLowerCase().includes(q);
  });

  return (
    <div className="grid md:grid-cols-[340px_1fr] gap-4">
      {/* thread list */}
      <div className="luxury-card p-0 overflow-hidden">
        <div className="p-3 border-b border-white/5 flex items-center gap-2">
          <Search size={14} className="text-zinc-500" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search…" className="bg-transparent flex-1 outline-none text-sm text-white" data-testid="inbox-search" />
          <button onClick={load} className="text-zinc-500 hover:text-white"><RefreshCw size={13} className={loading ? "animate-spin" : ""} /></button>
        </div>
        <div className="max-h-[65vh] overflow-y-auto">
          {filtered.length === 0 && <div className="p-6 text-center text-xs text-zinc-500">No conversations yet.</div>}
          {filtered.map((t) => (
            <button key={t.key} onClick={() => openThread(t)} className={`w-full text-left px-3 py-3 border-b border-white/5 hover:bg-white/[0.03] ${selected?.key === t.key ? "bg-red-500/5" : ""}`} data-testid={`thread-${t.key}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm text-white truncate flex-1">{t.lead_name || t.lead_email || `+${t.phone_digits}` || "Unknown"}</div>
                {t.unread > 0 && <span className="text-[10px] bg-red-600 text-white px-1.5 py-0.5 rounded-full">{t.unread}</span>}
              </div>
              <div className="text-[11px] text-zinc-500 flex items-center gap-1 mt-0.5">
                {t.last_channel === "email" ? <Mail size={10} /> : <MessageCircle size={10} />}
                <span className="truncate">{t.last_message}</span>
              </div>
              <div className="text-[10px] text-zinc-600 mt-0.5">{fmtWhen(t.last_at)}</div>
            </button>
          ))}
        </div>
      </div>

      {/* thread panel */}
      <div className="luxury-card p-0 min-h-[65vh] flex flex-col">
        {!selected ? (
          <div className="flex-1 flex items-center justify-center text-xs text-zinc-500 p-6">Select a conversation to see the full timeline.</div>
        ) : (
          <ThreadPanel selected={selected} thread={thread} onSent={() => openThread(selected)} />
        )}
      </div>
    </div>
  );
}

function ThreadPanel({ selected, thread, onSent }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("wa"); // wa | template | email
  const [templates, setTemplates] = useState([]);
  const [templateSid, setTemplateSid] = useState("");
  const [emailSubject, setEmailSubject] = useState("");

  // Compute 24h window
  const withinWindow = useMemo(() => {
    const cutoff = Date.now() - 24 * 3600 * 1000;
    return thread.some((m) => m.direction === "inbound" && new Date(m.created_at).getTime() >= cutoff);
  }, [thread]);

  useEffect(() => {
    if (mode === "template" && templates.length === 0) {
      axios.get(`${API}/admin/messaging/templates`, { headers: authHeaders() })
        .then((r) => setTemplates(r.data.filter((t) => t.approval_status === "approved")))
        .catch(() => {});
    }
  }, [mode]);

  // Auto-pick a sensible default mode when a new thread is selected
  useEffect(() => {
    setMode(withinWindow ? "wa" : (selected?.phone_digits ? "template" : "email"));
    setText(""); setEmailSubject(""); setTemplateSid("");
  }, [selected?.key, withinWindow]);

  const send = async () => {
    setBusy(true);
    try {
      const payload = {
        lead_id: selected.lead_id,
        phone: selected.phone_digits,
        email: selected.lead_email,
      };
      if (mode === "wa") {
        if (!text.trim()) { setBusy(false); return; }
        Object.assign(payload, { channel: "whatsapp", body: text });
      } else if (mode === "template") {
        if (!templateSid) { toast.error("Pick a template"); setBusy(false); return; }
        Object.assign(payload, {
          channel: "whatsapp",
          template_sid: templateSid,
          template_variables: { "1": selected.lead_name || "there" },
        });
      } else if (mode === "email") {
        if (!selected.lead_email) { toast.error("No email on file for this lead"); setBusy(false); return; }
        if (!emailSubject.trim() || !text.trim()) { toast.error("Subject and body required"); setBusy(false); return; }
        Object.assign(payload, { channel: "email", subject: emailSubject, body: text });
      }
      await axios.post(`${API}/admin/messaging/send`, payload, { headers: authHeaders() });
      setText(""); setEmailSubject(""); setTemplateSid("");
      toast.success("Sent");
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="p-4 border-b border-white/5">
        <div className="text-sm text-white font-medium">{selected.lead_name || selected.lead_email || `+${selected.phone_digits}`}</div>
        <div className="text-xs text-zinc-500 mt-0.5">
          {selected.phone_digits && <>+{selected.phone_digits}</>}
          {selected.phone_digits && selected.lead_email && " · "}
          {selected.lead_email}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[55vh]">
        {thread.map((m) => (
          <div key={m.id} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[75%] px-3 py-2 text-sm ${m.direction === "outbound" ? "bg-red-700/20 border border-red-700/40 text-white" : "bg-white/[0.04] border border-white/10 text-zinc-200"}`} style={{ borderRadius: 0 }}>
              <div className="flex items-center gap-1.5 text-[10px] text-zinc-500 mb-1 uppercase tracking-widest">
                {m.channel === "email" ? <Mail size={9} /> : <MessageCircle size={9} />} {m.channel}
                <span>·</span> {fmtWhen(m.created_at)}
                {m.direction === "outbound" && m.status && <span className="ml-auto">{m.status}</span>}
              </div>
              {m.subject && <div className="text-xs font-medium text-white mb-1">{m.subject}</div>}
              {m.body && <div className="whitespace-pre-wrap break-words">{m.body}</div>}
              {m.media?.length > 0 && (
                <div className="mt-2 grid grid-cols-2 gap-1.5">
                  {m.media.map((_md, i) => (
                    <img key={i} src={`${API}/admin/messaging/media/${m.id}?index=${i}`} alt="media" className="w-full h-28 object-cover border border-white/10" />
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {thread.length === 0 && <div className="text-center text-xs text-zinc-500 py-8">No messages yet in this thread.</div>}
      </div>
      <div className="p-3 border-t border-white/5 space-y-2">
        {/* Mode switcher */}
        <div className="flex gap-1.5 flex-wrap">
          <button onClick={() => setMode("wa")} disabled={!withinWindow || !selected.phone_digits} className={`text-[10px] px-2 py-1 border flex items-center gap-1 ${mode === "wa" ? "border-red-600 bg-red-700/10 text-white" : "border-white/10 text-zinc-400 hover:text-white disabled:opacity-30"}`} style={{ borderRadius: 0 }} data-testid="thread-mode-wa">
            <MessageCircle size={10} /> WA free-form {!withinWindow && "(24h closed)"}
          </button>
          <button onClick={() => setMode("template")} disabled={!selected.phone_digits} className={`text-[10px] px-2 py-1 border flex items-center gap-1 ${mode === "template" ? "border-red-600 bg-red-700/10 text-white" : "border-white/10 text-zinc-400 hover:text-white disabled:opacity-30"}`} style={{ borderRadius: 0 }} data-testid="thread-mode-template">
            <FileText size={10} /> WA template
          </button>
          <button onClick={() => setMode("email")} disabled={!selected.lead_email} className={`text-[10px] px-2 py-1 border flex items-center gap-1 ${mode === "email" ? "border-red-600 bg-red-700/10 text-white" : "border-white/10 text-zinc-400 hover:text-white disabled:opacity-30"}`} style={{ borderRadius: 0 }} data-testid="thread-mode-email">
            <Mail size={10} /> Email
          </button>
        </div>

        {mode === "wa" && withinWindow && (
          <div className="flex gap-2">
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Reply on WhatsApp (24h window open)…" className="flex-1 bg-transparent border border-white/10 text-white text-sm px-3 py-2 outline-none focus:border-red-600 resize-none" style={{ borderRadius: 0 }} data-testid="thread-reply-input" />
            <button onClick={send} disabled={busy || !text.trim()} className="btn-primary text-sm px-4 disabled:opacity-40" style={{ borderRadius: 0 }} data-testid="thread-send-btn"><Send size={14} /></button>
          </div>
        )}

        {mode === "template" && (
          <div className="flex gap-2">
            <select value={templateSid} onChange={(e) => setTemplateSid(e.target.value)} className="flex-1 bg-black border border-white/10 text-white text-xs px-2 py-2" style={{ borderRadius: 0 }} data-testid="thread-template-select">
              <option value="">— Pick an approved template —</option>
              {templates.map((t) => <option key={t.sid} value={t.sid}>{t.friendly_name}</option>)}
            </select>
            <button onClick={send} disabled={busy || !templateSid} className="btn-primary text-sm px-4 disabled:opacity-40" style={{ borderRadius: 0 }} data-testid="thread-send-template-btn"><Send size={14} /></button>
          </div>
        )}
        {mode === "template" && templates.length === 0 && (
          <div className="text-[11px] text-amber-400">No approved templates yet · create one in the Templates tab.</div>
        )}

        {mode === "email" && (
          <div className="space-y-2">
            <input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} placeholder="Subject" className="w-full bg-transparent border border-white/10 text-white text-sm px-3 py-2 outline-none focus:border-red-600" style={{ borderRadius: 0 }} data-testid="thread-email-subject" />
            <div className="flex gap-2">
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Write an email reply…" className="flex-1 bg-transparent border border-white/10 text-white text-sm px-3 py-2 outline-none focus:border-red-600 resize-none" style={{ borderRadius: 0 }} data-testid="thread-email-body" />
              <button onClick={send} disabled={busy || !emailSubject.trim() || !text.trim()} className="btn-primary text-sm px-4 disabled:opacity-40" style={{ borderRadius: 0 }} data-testid="thread-send-email-btn"><Send size={14} /></button>
            </div>
          </div>
        )}

        {mode === "wa" && !withinWindow && (
          <div className="text-[11px] text-amber-400 flex items-center gap-1.5">
            <Clock size={11} /> Free-form closed — pick a template or send an email above.
          </div>
        )}
      </div>
    </>
  );
}

// ============================================================
// Templates
// ============================================================
function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ friendly_name: "", body: "", category: "MARKETING", buttons: [] });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/messaging/templates`, { headers: authHeaders() });
      setTemplates(data);
    } catch (e) { toast.error("Could not load templates"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const addButton = () => {
    if (form.buttons.length >= 2) return;
    setForm({ ...form, buttons: [...form.buttons, { type: "URL", title: "", url: "" }] });
  };
  const updateButton = (i, patch) => {
    const b = [...form.buttons]; b[i] = { ...b[i], ...patch }; setForm({ ...form, buttons: b });
  };
  const removeButton = (i) => setForm({ ...form, buttons: form.buttons.filter((_, x) => x !== i) });

  const submit = async () => {
    if (!form.friendly_name.trim() || !form.body.trim()) return toast.error("Name and body required");
    try {
      const { data } = await axios.post(`${API}/admin/messaging/templates`, form, { headers: authHeaders() });
      toast.success(`Template submitted · ${data.approval_status || "received"}`);
      setCreating(false); setForm({ friendly_name: "", body: "", category: "MARKETING", buttons: [] });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs c-muted">{templates.length} templates · auto-refresh every 30s</div>
        <div className="flex gap-2">
          <button onClick={load} className="btn-ghost text-sm py-2 px-3"><RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh</button>
          <button onClick={() => setCreating(!creating)} className="btn-primary text-sm py-2 px-3" data-testid="template-new-btn"><Plus size={13} /> New</button>
        </div>
      </div>

      {creating && (
        <div className="luxury-card p-4 mb-4 space-y-3" data-testid="template-form">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Name (lowercase_with_underscores)</label>
            <input value={form.friendly_name} onChange={(e) => setForm({ ...form, friendly_name: e.target.value.toLowerCase().replace(/\s+/g, "_") })} className="w-full bg-transparent border border-white/10 text-white text-sm px-3 py-2 outline-none focus:border-red-600" style={{ borderRadius: 0 }} data-testid="template-name" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Body · use {"{{1}}"}, {"{{2}}"} for variables (e.g. name)</label>
            <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={5} className="w-full bg-transparent border border-white/10 text-white text-sm px-3 py-2 outline-none focus:border-red-600 resize-none" style={{ borderRadius: 0 }} data-testid="template-body" />
          </div>
          <div className="flex items-center gap-3">
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Category</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-black border border-white/10 text-white text-xs px-2 py-1.5">
              <option value="MARKETING">Marketing</option>
              <option value="UTILITY">Utility</option>
            </select>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Buttons (max 2)</label>
              <button onClick={addButton} disabled={form.buttons.length >= 2} className="text-xs text-red-500 disabled:opacity-40">+ Add button</button>
            </div>
            {form.buttons.map((b, i) => (
              <div key={i} className="grid grid-cols-[100px_1fr_1fr_28px] gap-2 mb-2">
                <select value={b.type} onChange={(e) => updateButton(i, { type: e.target.value })} className="bg-black border border-white/10 text-white text-xs px-2 py-1.5">
                  <option value="URL">URL</option>
                  <option value="QUICK_REPLY">Quick Reply</option>
                </select>
                <input value={b.title || ""} onChange={(e) => updateButton(i, { title: e.target.value })} placeholder="Button label" className="bg-transparent border border-white/10 text-white text-xs px-2 py-1.5 outline-none focus:border-red-600" />
                {b.type === "URL" ? (
                  <input value={b.url || ""} onChange={(e) => updateButton(i, { url: e.target.value })} placeholder="https://…" className="bg-transparent border border-white/10 text-white text-xs px-2 py-1.5 outline-none focus:border-red-600" />
                ) : (
                  <input value={b.id || ""} onChange={(e) => updateButton(i, { id: e.target.value })} placeholder="reply_id" className="bg-transparent border border-white/10 text-white text-xs px-2 py-1.5 outline-none focus:border-red-600" />
                )}
                <button onClick={() => removeButton(i)} className="text-zinc-500 hover:text-red-500 text-xs">✕</button>
              </div>
            ))}
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setCreating(false)} className="btn-ghost text-sm py-2 px-3">Cancel</button>
            <button onClick={submit} className="btn-primary text-sm py-2 px-3" data-testid="template-submit-btn">Submit for Approval</button>
          </div>
        </div>
      )}

      <div className="grid gap-3">
        {templates.map((t) => (
          <div key={t.sid} className="luxury-card p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="text-sm text-white font-medium">{t.friendly_name}</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">{t.sid} · {t.language} · {t.types.join(", ")}</div>
                <div className="text-xs text-zinc-300 mt-2 whitespace-pre-wrap">{t.body}</div>
              </div>
              <StatusBadge status={t.approval_status} />
            </div>
          </div>
        ))}
        {templates.length === 0 && !loading && <div className="luxury-card p-8 text-center text-xs c-muted">No templates yet.</div>}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const s = (status || "unknown").toLowerCase();
  const color = s === "approved" ? "border-emerald-600 text-emerald-400 bg-emerald-900/20" : s === "pending" || s === "received" ? "border-amber-600 text-amber-400 bg-amber-900/20" : s === "rejected" ? "border-red-600 text-red-400 bg-red-900/20" : "border-zinc-600 text-zinc-400 bg-zinc-900/20";
  const label = s === "approved" ? <><CheckIcon size={10} /> Approved</> : s;
  return <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border flex items-center gap-1 ${color}`} style={{ borderRadius: 0 }}>{label}</span>;
}

// ============================================================
// Bulk sender
// ============================================================
function BulkSender() {
  const [channel, setChannel] = useState("email");
  const [templates, setTemplates] = useState([]);
  const [templateSid, setTemplateSid] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterSource, setFilterSource] = useState("All");
  const [busy, setBusy] = useState(false);
  const [testRecipient, setTestRecipient] = useState("");

  useEffect(() => {
    if (channel === "whatsapp") {
      axios.get(`${API}/admin/messaging/templates`, { headers: authHeaders() }).then((r) => setTemplates(r.data.filter((t) => t.approval_status === "approved"))).catch(() => {});
    }
  }, [channel]);

  const send = async (isTest) => {
    if (channel === "whatsapp" && !templateSid) return toast.error("Pick an approved template");
    if (channel === "email" && (!subject.trim() || !body.trim())) return toast.error("Subject and body required");
    if (isTest && !testRecipient.trim()) return toast.error("Enter a test recipient");
    if (!isTest && !window.confirm(`Send this ${channel} to matching leads? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const body_html = channel === "email" ? body.replace(/\n/g, "<br/>") : null;
      const { data } = await axios.post(`${API}/admin/messaging/bulk`, {
        channel,
        template_sid: templateSid || null,
        template_variables_map: templateSid ? { "1": "name" } : null,
        subject: subject || null,
        body_html,
        filter_status: filterStatus,
        filter_source: filterSource,
        test_recipient: isTest ? testRecipient.trim() : null,
      }, { headers: authHeaders() });
      toast.success(isTest ? "Test sent" : `Campaign started · ${data.total} recipient(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl">
      <div className="flex gap-2 mb-4">
        <button onClick={() => setChannel("email")} className={`text-xs px-3 py-2 border flex items-center gap-1.5 ${channel === "email" ? "border-red-600 bg-red-700/10 text-white" : "border-white/10 text-zinc-400"}`} style={{ borderRadius: 0 }} data-testid="bulk-channel-email"><Mail size={13} /> Email</button>
        <button onClick={() => setChannel("whatsapp")} className={`text-xs px-3 py-2 border flex items-center gap-1.5 ${channel === "whatsapp" ? "border-red-600 bg-red-700/10 text-white" : "border-white/10 text-zinc-400"}`} style={{ borderRadius: 0 }} data-testid="bulk-channel-whatsapp"><MessageCircle size={13} /> WhatsApp</button>
      </div>

      <div className="luxury-card p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Filter by status</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="w-full bg-black border border-white/10 text-white text-xs px-2 py-2">
              {["All", "New", "Interested", "Not Interested", "Connect Later", "DNP", "Payment Done", "Proposal Sent", "Registered", "Not Qualified"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Filter by source</label>
            <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)} className="w-full bg-black border border-white/10 text-white text-xs px-2 py-2">
              {["All", "contact", "vsl_qualify", "WEBINAR", "elite_payment", "booking_disqualified", "direct_message"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {channel === "whatsapp" ? (
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Approved template</label>
            <select value={templateSid} onChange={(e) => setTemplateSid(e.target.value)} className="w-full bg-black border border-white/10 text-white text-xs px-2 py-2" data-testid="bulk-template-select">
              <option value="">— Pick a template —</option>
              {templates.map((t) => <option key={t.sid} value={t.sid}>{t.friendly_name}</option>)}
            </select>
            {templates.length === 0 && <div className="text-[11px] text-amber-400 mt-2">No approved templates yet. Create one in the Templates tab.</div>}
          </div>
        ) : (
          <>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Subject</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full bg-transparent border border-white/10 text-white text-sm px-3 py-2 outline-none focus:border-red-600" style={{ borderRadius: 0 }} data-testid="bulk-email-subject" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Body (line breaks preserved · HTML allowed)</label>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} className="w-full bg-transparent border border-white/10 text-white text-sm px-3 py-2 outline-none focus:border-red-600 resize-none" style={{ borderRadius: 0 }} data-testid="bulk-email-body" />
            </div>
          </>
        )}

        <div className="pt-3 border-t border-white/5 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Test recipient ({channel === "email" ? "email" : "WhatsApp number"})</label>
            <div className="flex gap-2">
              <input value={testRecipient} onChange={(e) => setTestRecipient(e.target.value)} placeholder={channel === "email" ? "you@brand.com" : "+91 98…"} className="flex-1 bg-transparent border border-white/10 text-white text-sm px-3 py-2 outline-none focus:border-red-600" style={{ borderRadius: 0 }} />
              <button onClick={() => send(true)} disabled={busy} className="btn-ghost text-sm py-2 px-3 disabled:opacity-40" data-testid="bulk-test-btn">Send Test</button>
            </div>
          </div>
          <button onClick={() => send(false)} disabled={busy} className="btn-primary text-sm py-2.5 px-4 w-full justify-center disabled:opacity-40" data-testid="bulk-blast-btn">
            <Send size={13} /> Blast to matching leads
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Direct (single recipient)
// ============================================================
function DirectSender() {
  const [channel, setChannel] = useState("whatsapp");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templateSid, setTemplateSid] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (channel === "whatsapp") {
      axios.get(`${API}/admin/messaging/templates`, { headers: authHeaders() }).then((r) => setTemplates(r.data.filter((t) => t.approval_status === "approved"))).catch(() => {});
    }
  }, [channel]);

  const send = async () => {
    if (channel === "whatsapp" && !phone.trim()) return toast.error("Enter a WhatsApp number");
    if (channel === "email" && !email.trim()) return toast.error("Enter an email address");
    if (channel === "whatsapp" && !templateSid && !body.trim()) return toast.error("Type a message or pick a template");
    if (channel === "email" && (!subject.trim() || !body.trim())) return toast.error("Subject and body required");
    setBusy(true);
    try {
      await axios.post(`${API}/admin/messaging/send`, {
        name: name.trim() || null,
        phone: phone.trim() || null,
        email: email.trim() || null,
        channel,
        body: body.trim() || null,
        subject: subject.trim() || null,
        template_sid: channel === "whatsapp" && templateSid ? templateSid : null,
        template_variables: templateSid ? { "1": name.trim() || "there" } : null,
      }, { headers: authHeaders() });
      toast.success("Sent · conversation saved to Inbox");
      setBody(""); setSubject(""); setTemplateSid("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };

  const inputCls = "w-full bg-transparent border border-white/10 text-white text-sm px-3 py-2 outline-none focus:border-red-600";
  const labelCls = "text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block";

  return (
    <div className="max-w-2xl" data-testid="direct-sender">
      <div className="flex gap-2 mb-4">
        <button onClick={() => setChannel("whatsapp")} className={`text-xs px-3 py-2 border flex items-center gap-1.5 ${channel === "whatsapp" ? "border-red-600 bg-red-700/10 text-white" : "border-white/10 text-zinc-400"}`} style={{ borderRadius: 0 }} data-testid="direct-channel-whatsapp"><MessageCircle size={13} /> WhatsApp</button>
        <button onClick={() => setChannel("email")} className={`text-xs px-3 py-2 border flex items-center gap-1.5 ${channel === "email" ? "border-red-600 bg-red-700/10 text-white" : "border-white/10 text-zinc-400"}`} style={{ borderRadius: 0 }} data-testid="direct-channel-email"><Mail size={13} /> Email</button>
      </div>

      <div className="luxury-card p-4 space-y-3">
        <div>
          <label className={labelCls}>Name (optional — labels the thread)</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Rahul from the webinar" className={inputCls} style={{ borderRadius: 0 }} data-testid="direct-name" />
        </div>

        {channel === "whatsapp" ? (
          <div>
            <label className={labelCls}>WhatsApp number</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98…" className={inputCls} style={{ borderRadius: 0 }} data-testid="direct-phone" />
          </div>
        ) : (
          <div>
            <label className={labelCls}>Email address</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="lead@example.com" className={inputCls} style={{ borderRadius: 0 }} data-testid="direct-email" />
          </div>
        )}

        {channel === "whatsapp" ? (
          <>
            <div>
              <label className={labelCls}>Message (free-form — only delivers if they messaged you in the last 24h)</label>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} disabled={!!templateSid} className={`${inputCls} resize-none disabled:opacity-40`} style={{ borderRadius: 0 }} data-testid="direct-wa-body" />
            </div>
            <div>
              <label className={labelCls}>Or use an approved template (delivers anytime)</label>
              <select value={templateSid} onChange={(e) => setTemplateSid(e.target.value)} className="w-full bg-black border border-white/10 text-white text-xs px-2 py-2" data-testid="direct-template-select">
                <option value="">— No template (free-form) —</option>
                {templates.map((t) => <option key={t.sid} value={t.sid}>{t.friendly_name}</option>)}
              </select>
              {templates.length === 0 && <div className="text-[11px] text-amber-400 mt-2">No approved templates yet. Create one in the Templates tab.</div>}
            </div>
          </>
        ) : (
          <>
            <div>
              <label className={labelCls}>Subject</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} style={{ borderRadius: 0 }} data-testid="direct-email-subject" />
            </div>
            <div>
              <label className={labelCls}>Body (line breaks preserved · HTML allowed)</label>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} className={`${inputCls} resize-none`} style={{ borderRadius: 0 }} data-testid="direct-email-body" />
            </div>
          </>
        )}

        <button onClick={send} disabled={busy} className="btn-primary text-sm py-2.5 px-4 w-full justify-center disabled:opacity-40" data-testid="direct-send-btn">
          <Send size={13} /> Send {channel === "email" ? "Email" : "WhatsApp"}
        </button>
        <p className="text-[11px] text-zinc-500">New numbers/emails are added to the Leads tab automatically, and the conversation appears in the Inbox.</p>
      </div>
    </div>
  );
}
